import React, { useState } from "react";
import { withEmbedParam, withoutEmbedParam } from "../../shared/embedMode";

// CRA replaces process.env.PUBLIC_URL at build time with the `homepage` base ("/app").
const APP_BASE = process.env.PUBLIC_URL || "";

/**
 * Normalize whatever the assistant produced (a relative router path, or an absolute URL —
 * possibly with a hallucinated domain) into a react-router path WITHOUT the app basename,
 * so `navigate()` (which re-adds the basename) doesn't double it and a wrong domain is
 * simply ignored (we only ever link to our own app pages).
 */
export function toRouterPath(target) {
  if (!target) return null;
  let pathAndQuery = String(target).trim();
  if (/^https?:\/\//i.test(pathAndQuery)) {
    try {
      const u = new URL(pathAndQuery);
      pathAndQuery = u.pathname + u.search + u.hash;
    } catch {
      // leave as-is
    }
  }
  if (APP_BASE && pathAndQuery.startsWith(APP_BASE)) {
    pathAndQuery = pathAndQuery.slice(APP_BASE.length) || "/";
  }
  if (!pathAndQuery.startsWith("/")) pathAndQuery = "/" + pathAndQuery;
  return pathAndQuery;
}

/**
 * Full same-origin URL for the embedded webview (origin + basename + router path).
 *
 * `embed=1` просит layout отрисовать одну страницу — без сайдбара, шапки
 * и виджета ассистента. Иначе в рамке 260px видно всё, кроме страницы,
 * ради которой предпросмотр и открывали.
 */
export function toEmbedUrl(routerPath) {
  return `${window.location.origin}${APP_BASE}${withEmbedParam(routerPath)}`;
}

/** Pull markdown links `[label](url)` out of assistant text as {label, to} (path only). */
export function extractInternalLinks(text) {
  if (!text) return [];
  const re = /\[([^\]]+)\]\(([^)]+)\)/g;
  const out = [];
  let m;
  while ((m = re.exec(text)) !== null) {
    const to = toRouterPath(m[2]);
    if (to) out.push({ label: m[1], to });
  }
  return out;
}

/**
 * A CRM deep-link rendered inside the chat: a small embedded webview preview of the target
 * page (same-origin iframe — token is shared via storage, so it authenticates) plus a
 * "Показать полностью" button that opens the page via client-side navigation.
 */
export default function AssistantLinkCard({ link, onOpen }) {
  const [showPreview, setShowPreview] = useState(true);
  const embedUrl = toEmbedUrl(link.to);

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <span style={styles.label} title={link.to}>
          ↗ {link.label}
        </span>
        <div style={styles.actions}>
          <button type="button" style={styles.toggleBtn} onClick={() => setShowPreview((v) => !v)}>
            {showPreview ? "Скрыть" : "Предпросмотр"}
          </button>
          {/* Снимаем embed=1: ассистент мог прислать ссылку уже с ним,
              и страница открылась бы целиком без сайдбара. */}
          <button
            type="button"
            style={styles.openBtn}
            onClick={() => onOpen(withoutEmbedParam(link.to))}
          >
            Показать полностью
          </button>
        </div>
      </div>
      {showPreview && (
        <div style={styles.frameWrap}>
          <iframe
            src={embedUrl}
            title={link.label}
            style={styles.frame}
            loading="lazy"
          />
          <div style={styles.frameHint}>Предпросмотр страницы — кликните «Показать полностью», чтобы открыть.</div>
        </div>
      )}
    </div>
  );
}

const styles = {
  card: {
    marginTop: 8,
    border: "1px solid var(--c-border)",
    borderRadius: 16,
    overflow: "hidden",
    background: "var(--c-input)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
    padding: "8px 10px",
  },
  label: { fontSize: 14, fontWeight: 700, color: "var(--c-text)", overflow: "hidden", textOverflow: "ellipsis" },
  actions: { display: "flex", gap: 6, flexShrink: 0 },
  toggleBtn: {
    fontSize: 13,
    padding: "4px 8px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "transparent",
    color: "var(--c-text-sub)",
    cursor: "pointer",
  },
  openBtn: {
    fontSize: 13,
    padding: "4px 10px",
    borderRadius: 99,
    border: "none",
    background: "var(--c-accent)",
    color: "var(--c-accent-on)",
    cursor: "pointer",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  frameWrap: { borderTop: "1px solid var(--c-border)" },
  frame: {
    width: "100%",
    height: 260,
    border: "none",
    display: "block",
    background: "var(--c-card)",
  },
  frameHint: { fontSize: 12, color: "var(--c-text-sub)", padding: "4px 10px" },
};
